import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

const STORAGE_KEY = "jd_age_verified_v1";

export default function AgeGate() {
  const [open, setOpen] = useState(false);
  const [denied, setDenied] = useState(false);

  useEffect(() => {
    const verified = typeof window !== "undefined" && window.localStorage.getItem(STORAGE_KEY);
    if (!verified) {
      setOpen(true);
      document.documentElement.style.overflow = "hidden";
    }
    return () => {
      document.documentElement.style.overflow = "";
    };
  }, []);

  const accept = () => {
    window.localStorage.setItem(STORAGE_KEY, "1");
    document.documentElement.style.overflow = "";
    setOpen(false);
  };

  const decline = () => {
    setDenied(true);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="age-gate"
          role="dialog"
          aria-modal="true"
          aria-labelledby="age-gate-title"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="fixed inset-0 z-[100] flex items-center justify-center px-6"
          style={{ background: "rgba(8, 7, 6, 0.94)", backdropFilter: "blur(16px)", WebkitBackdropFilter: "blur(16px)" }}
        >
          {/* Ambient glow */}
          <div className="hero-glow pointer-events-none" style={{ top: "10%", left: "50%", transform: "translateX(-50%)", opacity: 0.5 }} />

          <motion.div
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-full max-w-2xl border p-10 md:p-14"
            style={{ borderColor: "var(--brass)", background: "var(--surface)" }}
          >
            {/* Corner marks */}
            {[
              { top: -1, left: -1, borderTop: "1px solid var(--amber)", borderLeft: "1px solid var(--amber)" },
              { top: -1, right: -1, borderTop: "1px solid var(--amber)", borderRight: "1px solid var(--amber)" },
              { bottom: -1, left: -1, borderBottom: "1px solid var(--amber)", borderLeft: "1px solid var(--amber)" },
              { bottom: -1, right: -1, borderBottom: "1px solid var(--amber)", borderRight: "1px solid var(--amber)" },
            ].map((s, i) => (
              <span key={i} aria-hidden style={{ position: "absolute", width: 22, height: 22, ...s }} />
            ))}

            <div className="flex items-center gap-3 mb-8">
              <svg width="26" height="26" viewBox="0 0 40 40" fill="none">
                <path d="M8 8 L20 32 L32 8" stroke="var(--brass)" strokeWidth="1.4" />
                <circle cx="20" cy="20" r="3" fill="var(--amber)" />
              </svg>
              <span className="eyebrow">Johnny Dram</span>
            </div>

            {denied ? (
              <>
                <h2 id="age-gate-title" className="font-display text-4xl md:text-5xl leading-[1.02] tracking-tight text-bone mb-6">
                  Come back <em className="italic" style={{ color: "var(--amber)" }}>later</em>.
                </h2>
                <p className="font-body text-lg text-bone-dim mb-10 max-w-md">
                  Johnny Dram is a spirits publication written for adults. We'll be here when you're of legal drinking age — the dram will keep.
                </p>
                <div className="flex flex-wrap items-center gap-4">
                  <button
                    onClick={() => setDenied(false)}
                    data-testid="age-gate-back"
                    className="btn-outline"
                  >
                    Go Back
                  </button>
                  <a
                    href="https://www.responsibility.org/"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="age-gate-responsibility"
                    className="eyebrow hover:text-[var(--amber)] transition-colors"
                    style={{ color: "var(--bone-dim)" }}
                  >
                    Drink Responsibly →
                  </a>
                </div>
              </>
            ) : (
              <>
                <div className="chapter-numeral text-5xl mb-4">21+</div>
                <h2 id="age-gate-title" className="font-display text-4xl md:text-5xl leading-[1.02] tracking-tight text-bone mb-6">
                  A quiet word <em className="italic" style={{ color: "var(--amber)" }}>before you enter</em>.
                </h2>
                <p className="font-body text-lg text-bone-dim mb-10 max-w-md">
                  Johnny Dram explores exceptional whiskey, premium spirits, unforgettable experiences, and the lifestyle that brings them together. By entering, you confirm you are of legal drinking age in your country.
                </p>

                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
                  <button
                    onClick={accept}
                    data-testid="age-gate-accept"
                    className="btn-solid justify-center"
                  >
                    I am 21 or older
                  </button>
                  <button
                    onClick={decline}
                    data-testid="age-gate-decline"
                    className="btn-outline justify-center"
                  >
                    I am under 21
                  </button>
                </div>

                <div className="mt-10 pt-6 border-t flex items-center justify-between gap-4" style={{ borderColor: "var(--hairline)" }}>
                  <span className="eyebrow" style={{ color: "var(--bone-dim)" }}>Please drink responsibly.</span>
                </div>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
